/* eslint-disable no-param-reassign */
// @flow
import React, { useState } from 'react';
import { useCombobox, useMultipleSelection } from 'downshift';
import classNames from 'classnames';
import { t } from 'javascripts/utils/withTranslation';
import Icon from 'components/_particles/icon/react/Icon';
import MediaButton from 'components/atoms/media-button/react/MediaButton';
import Checkbox from 'components/atoms/checkbox/react/Checkbox';
import Label from 'components/atoms/label/react/Label';

export type Option = {
  text?: string,
  value: string,
  selected?: boolean,
  number?: string,
};

export type Props = {
  options: Option[],
  selectedItems: Option[],
  setSelectedItems: (items: Option[]) => void,
  id?: string,
  name?: string,
  placeholder?: string,
  labelText?: string,
  large?: boolean,
};

function getFilteredItems(
  selectedItems?: Option[],
  inputValue?: string,
  options: Option[]
) {
  const lowerCasedInputValue = inputValue?.toLowerCase() || '';

  return options.filter((option) =>
    option.text?.toLowerCase().includes(lowerCasedInputValue)
  );
}

const ControlledMultiselect = ({
  options,
  selectedItems,
  setSelectedItems,
  id,
  name,
  placeholder,
  labelText,
  large,
}: Props): React$MixedElement => {
  const [inputValue, setInputValue] = useState('');
  const [isKeyboardNavigation, setIsKeyboardNavigation] =
    React.useState<boolean>(false);
  const [announcement, setAnnouncement] = React.useState<string>('');
  const items = React.useMemo(
    () => getFilteredItems(selectedItems, inputValue, options),
    [selectedItems, inputValue]
  );

  const { getSelectedItemProps, getDropdownProps, removeSelectedItem } =
    useMultipleSelection({
      selectedItems,
      onStateChange({ selectedItems: newSelectedItems, type }) {
        switch (type) {
          case useMultipleSelection.stateChangeTypes
            .SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            setSelectedItems(newSelectedItems);
            break;
          default:
            break;
        }
      },
    });

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    clearSelection,
    openMenu,
  } = useCombobox({
    items,
    inputValue,
    selectedItem: null,
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          // Keep index of selected item after selection
          return {
            ...changes,
            ...(changes.selectedItem && {
              isOpen: true,
              highlightedIndex: state.highlightedIndex,
            }),
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            ...(changes.selectedItem && { isOpen: true, highlightedIndex: 0 }),
          };
        default:
          return changes;
      }
    },
    onStateChange({
      inputValue: newInputValue,
      type,
      selectedItem: newSelectedItem,
    }) {
      switch (type) {
        case useCombobox.stateChangeTypes.keyDownSpaceButton:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (newSelectedItem && !selectedItems?.includes(newSelectedItem)) {
            setSelectedItems([...(selectedItems || []), newSelectedItem]);
          } else {
            setSelectedItems(
              selectedItems?.filter((item) => item !== newSelectedItem)
            );
          }

          break;
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue);
          break;
        default:
          break;
      }
    },
    onHighlightedIndexChange: ({ type }) => {
      // Check if the change was caused by keyboard navigation
      const keyboardTypes = [
        useCombobox.stateChangeTypes.InputKeyDownArrowDown,
        useCombobox.stateChangeTypes.InputKeyDownArrowUp,
        useCombobox.stateChangeTypes.InputKeyDownHome,
        useCombobox.stateChangeTypes.InputKeyDownEnd,
      ];
      setIsKeyboardNavigation(keyboardTypes.includes(type));
    },
  });

  // Announce navigation instructions when dropdown opens
  React.useEffect(() => {
    if (isOpen) {
      setAnnouncement(
        t(
          'Navigieren Sie mit den Pfeiltasten. Bestätigen Sie die Auswahl mit der Eingabetaste.'
        )
      );
    } else {
      setAnnouncement('');
    }
  }, [isOpen]);

  // Announce selected items count changes
  React.useEffect(() => {
    const count = selectedItems?.length || 0;
    let announcementMessage = '';
    if (count === 0) {
      announcementMessage = t('Keine Elemente ausgewählt');
    } else if (count === 1) {
      announcementMessage = t('1 Element ausgewählt');
    } else {
      announcementMessage = t(`${count} Elemente ausgewählt`);
    }

    setAnnouncement(announcementMessage);
  }, [selectedItems]);

  const renderSelectedItem = (selectedItemForRender: Option, index: number) => (
    <span
      key={`selected-item-${selectedItemForRender.value}${index}`}
      className="multiselect__chip"
    >
      {selectedItemForRender.text}
    </span>
  );

  const handleToggle = (item: Option) => {
    if (selectedItems?.map((i) => i.value).includes(item.value)) {
      removeSelectedItem(item);
    } else {
      setSelectedItems([...(selectedItems || []), item]);
    }
  };

  const handleInputKeyDown = (event: KeyboardEvent) => {
    // Open dropdown with Space key when closed
    if (event.key === ' ' && !isOpen) {
      event.preventDefault();
      const toggleButton =
        event.target?.parentElement?.querySelector('[aria-label]');
      if (toggleButton) {
        toggleButton.click();
      }
    }

    // Open Menu with Enter key when closed
    if (event.key === 'Enter' && !isOpen) {
      event.preventDefault();
      if (highlightedIndex === -1) {
        // $FlowFixMe
        (event.nativeEvent: any).preventDownshiftDefault = true;
        openMenu();
      }
    }
  };

  const getInputPlaceholder = () => {
    if (isOpen) {
      return t('Auswählen');
    }
    if (selectedItems?.length === 0) {
      return placeholder;
    }
    return undefined;
  };

  return (
    <div
      className={classNames(
        'multiselect multiselect--init',
        large && 'multiselect--large'
      )}
    >
      <Label {...getLabelProps()}>{labelText}</Label>
      <div className="multiselect__grid">
        <div
          className={classNames(
            'multiselect__chips',
            (selectedItems.length === 0 || !selectedItems) &&
              'multiselect__chips--hidden'
          )}
        >
          {selectedItems?.length < options?.length ? (
            <>
              <span className="u-hidden-visually">{t('Ausgewählt')}:</span>
              {selectedItems?.map((item, i) => renderSelectedItem(item, i))}
            </>
          ) : (
            <span>{placeholder}</span>
          )}
        </div>

        <div className="multiselect__input-wrap">
          <input
            placeholder={getInputPlaceholder()}
            className={classNames(
              'select multiselect__select',
              large && 'select--large'
            )}
            readOnly
            {...getInputProps(
              getDropdownProps({
                preventKeyAction: isOpen,
                onKeyDown: handleInputKeyDown,
              })
            )}
          />
          <button
            aria-label={t('Menü öffnen und schließen')}
            className="button button--icon multiselect__toggle"
            type="button"
            {...getToggleButtonProps()}
          >
            <Icon icon="arrow-down" />
          </button>
        </div>
      </div>

      <ul
        className={classNames(
          'multiselect__menu dropdown-menu__list',
          isOpen && 'dropdown-menu multiselect__dropdown--open',
          !isOpen && 'hidden'
        )}
        {...getMenuProps()}
      >
        {isOpen &&
          items.map((item, index) => (
            <li
              className={classNames(
                'dropdown-menu__list-item',
                'multiselect__item',
                highlightedIndex === index &&
                  'dropdown-menu__list-item--active',
                highlightedIndex === index &&
                  isKeyboardNavigation &&
                  'multiselect__item--keyboard-focus',
                highlightedIndex === index &&
                  !isKeyboardNavigation &&
                  'multiselect__item--hover'
              )}
              key={`${item.value}${index}`}
              {...getItemProps({
                item,
                index,
              })}
            >
              <div
                className={classNames(
                  'dropdown-menu__link',
                  highlightedIndex === index && 'current'
                )}
              >
                <span className="dropdown-menu__link">
                  <span className="dropdown-menu__text">
                    <div
                      className={classNames(
                        'checkbox',
                        selectedItems
                          .map((i) => i.value)
                          .includes(item.value) && 'checkbox--checked'
                      )}
                    >
                      <span className="checkbox__indicator">
                        <Icon
                          icon="check"
                          className="checkbox__indicator-icon"
                        />
                        <span className="u-hidden-visually">
                          {selectedItems
                            .map((i) => i.value)
                            .includes(item.value) && 'checkbox--checked'
                            ? t('Ausgewählt')
                            : ''}
                        </span>
                      </span>
                      <span className="checkbox__label">
                        <span>{item.text || ''}</span>
                        {item.number && <span> ({item.number})</span>}
                      </span>
                    </div>
                  </span>
                </span>
              </div>
            </li>
          ))}
      </ul>

      {/* Screen reader announcement */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="u-hidden-visually"
      >
        {announcement}
      </div>
    </div>
  );
};

export default ControlledMultiselect;
